import {
  observationPositionKey,
  type GameReplayState,
  type GameReplayStore,
  type ObservationAnalysisPosition,
} from './game-replay-store';

type AnalysisClient = Pick<GameReplayStore, 'refreshAiStatus' | 'analyzeObservation' | 'cancelAi'>;
type SessionState = {
  readonly gameId: string | null;
  readonly enabled: boolean;
  readonly initializing: boolean;
};

/** Rendering rejects results for the previous board even before effect cleanup cancels its job. */
export function presentObservationAi(
  state: SessionState,
  gameId: string,
  position: ObservationAnalysisPosition | null,
  suspended: boolean,
  replayState: GameReplayState | null,
) {
  const key = position === null ? null : observationPositionKey(position);
  const enabled = state.gameId === gameId && state.enabled;
  const matching =
    enabled &&
    !state.initializing &&
    !suspended &&
    position?.gameId === gameId &&
    key !== null &&
    replayState?.observationAiPosition !== null &&
    replayState?.observationAiPosition !== undefined &&
    observationPositionKey(replayState.observationAiPosition) === key;
  return {
    enabled,
    analysis: matching ? replayState.aiAnalysis : null,
    pending:
      enabled &&
      !suspended &&
      position !== null &&
      (state.initializing || !matching || Boolean(replayState?.aiPending)),
    error: matching ? replayState.aiError : null,
  };
}

/** The teacher's AI switch survives position changes; individual analysis jobs do not. */
export class ObservationAiSession {
  private state: SessionState = { gameId: null, enabled: false, initializing: false };
  private position: ObservationAnalysisPosition | null = null;
  private suspended = false;
  private requestedKey: string | null = null;
  private toggleGeneration = 0;
  private readonly listeners = new Set<() => void>();

  constructor(private readonly client: AnalysisClient | undefined) {}

  readonly snapshot = (): SessionState => this.state;
  readonly subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  setContext(
    gameId: string,
    position: ObservationAnalysisPosition | null,
    suspended: boolean,
  ): void {
    this.position = position?.gameId === gameId ? position : null;
    this.suspended = suspended;
    if (this.state.gameId !== gameId) {
      this.toggleGeneration += 1;
      this.cancelRequest();
      this.publish({ gameId, enabled: false, initializing: false });
      return;
    }
    const key = this.position === null ? null : observationPositionKey(this.position);
    if (this.requestedKey !== null && (suspended || this.requestedKey !== key))
      this.cancelRequest();
    this.synchronize();
  }

  readonly toggle = async (): Promise<void> => {
    if (this.state.enabled) {
      this.stop();
      return;
    }
    if (this.client === undefined || this.state.gameId === null) return;
    const generation = ++this.toggleGeneration;
    this.publish({ ...this.state, enabled: true, initializing: true });
    // Display the enabled switch immediately, so a second click can cancel slow initialization.
    await this.client.refreshAiStatus().catch(() => undefined);
    if (generation !== this.toggleGeneration || !this.state.enabled) return;
    this.publish({ ...this.state, initializing: false });
    this.synchronize();
  };

  readonly retry = (): void => {
    if (!this.state.enabled) {
      void this.toggle();
      return;
    }
    this.cancelRequest();
    this.synchronize();
  };

  readonly stop = (): void => {
    this.toggleGeneration += 1;
    this.cancelRequest();
    this.publish({ ...this.state, enabled: false, initializing: false });
  };

  private cancelRequest(): void {
    this.requestedKey = null;
    this.client?.cancelAi();
  }

  private synchronize(): void {
    if (
      !this.state.enabled ||
      this.state.initializing ||
      this.suspended ||
      this.position === null ||
      this.client === undefined
    )
      return;
    const key = observationPositionKey(this.position);
    if (key === this.requestedKey) return;
    this.requestedKey = key;
    // The replay store owns generation checks, cancellation, polling, and recoverable errors.
    void this.client.analyzeObservation(this.position).catch(() => undefined);
  }

  private publish(next: SessionState): void {
    this.state = next;
    for (const listener of this.listeners) listener();
  }
}
